import {addHours} from 'date-fns';
import {Alert, ProgressBar} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  type FinancingEndingBulkOperationItemResponseBody,
  type FinancingEndingBulkOperationResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {parseDate} from 'shared/web';

import {FINANCING_ENDING_DELIVERY_RESULT_DEADLINE_HOURS} from '../../constants/FINANCING_ENDING_DELIVERY_RESULT_DEADLINE_HOURS';
import {FINANCING_ENDING_STATUS_SEPARATOR} from '../../constants/FINANCING_ENDING_STATUS_SEPARATOR';
import {getFinancingEndingOperationVerdict} from '../../utils/getFinancingEndingOperationVerdict';

export type FinancingEndingOperationVerdictProps = {
  operation: FinancingEndingBulkOperationResponseBody;
  items: FinancingEndingBulkOperationItemResponseBody[];
};

/**
 * The verdict and its summary come from `getFinancingEndingOperationVerdict`. The summary is the
 * second line of the verdict. A `neutral` verdict is a plain text with the time by which the backend
 * settles the pending outcomes, not a coloured result. Under the verdict a context line shows each
 * known context part as its own element.
 */
export function FinancingEndingOperationVerdict(props: FinancingEndingOperationVerdictProps) {
  const formatDateTime = useDateTimeFormatter();
  const verdict = getFinancingEndingOperationVerdict(props.operation, props.items);

  const deliveryDeadline = isNil(props.operation.finishedAt)
    ? null
    : i18n.t('entity.businessCase.financingEnding.operation.verdict.deliveryDeadline', {
        deadline: formatDateTime(
          'dateTimeShort',
          addHours(
            parseDate(props.operation.finishedAt),
            FINANCING_ENDING_DELIVERY_RESULT_DEADLINE_HOURS
          )
        ),
      });

  const finishedAt = isNil(props.operation.finishedAt)
    ? undefined
    : i18n.t('entity.businessCase.financingEnding.operation.verdict.finishedAt', {
        finishedAt: formatDateTime('dateTimeShort', parseDate(props.operation.finishedAt)),
      });

  const contextLine = (
    <Show when={verdict.contextParts.length > 0}>
      <Text
        size="small"
        color="secondary"
        data-testid={testIds.businessCase.financingEnding('operationContext')}
      >
        <HStack wrap>
          {verdict.contextParts.map((part, partIndex) => (
            <HStack key={part.key}>
              <Show when={partIndex > 0}>
                <Text
                  inline
                  size="small"
                  color="secondary"
                  role="presentation"
                  preWrap
                  data-testid={testIds.businessCase.financingEnding('operationContextSeparator')}
                >
                  {FINANCING_ENDING_STATUS_SEPARATOR}
                </Text>
              </Show>
              <Text
                inline
                size="small"
                color="secondary"
                data-testid={testIds.businessCase.financingEnding('operationContextPart')}
              >
                {part.text}
              </Text>
            </HStack>
          ))}
        </HStack>
      </Text>
    </Show>
  );

  if (verdict.variant === 'neutral') {
    const neutralTestId = testIds.businessCase.financingEnding('operationVerdict-neutral');

    return (
      <Box role="status">
        <VStack spacing={1} data-testid={neutralTestId}>
          <Heading size={5} data-testid={`${neutralTestId}-title`}>
            {verdict.message}
          </Heading>
          <Text size="small" data-testid={`${neutralTestId}-summary`}>
            {verdict.summary}
          </Text>
          <Show when={isNotNilOrEmpty(finishedAt) ? finishedAt : null}>
            {(finishedAt) => (
              <Text size="xSmall" color="secondary">
                {finishedAt}
              </Text>
            )}
          </Show>
          <Show when={isNotNilOrEmpty(deliveryDeadline) ? deliveryDeadline : null}>
            {(deliveryDeadline) => (
              <Text size="xSmall" color="secondary" data-testid={`${neutralTestId}-deadline`}>
                {deliveryDeadline}
              </Text>
            )}
          </Show>
          {contextLine}
        </VStack>
      </Box>
    );
  }

  return (
    <VStack spacing={2}>
      <Alert
        layout="detailed"
        variant={verdict.variant}
        title={verdict.message}
        message={verdict.summary}
        secondaryMessage={finishedAt}
        isLoading={verdict.isRunning}
        data-testid={testIds.businessCase.financingEnding(`operationVerdict-${verdict.variant}`)}
      />
      {contextLine}
      <Show when={verdict.isRunning && props.operation.totalCount > 0}>
        <ProgressBar
          percentage={verdict.processedCount / props.operation.totalCount}
          data-testid={testIds.businessCase.financingEnding('operationProgress')}
        />
      </Show>
    </VStack>
  );
}
